<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import AnewsStorageFileList from '@/components/admin/user-documents/anews-storage-file-list.vue';
import AnewsStorageUploadFileFolderForm from '@/components/admin/user-documents/anews-storage-upload-file-folder-form.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import UserDocument from '@/components/layouts/user-document.vue';
import { formatDateForStorage } from '@/utils/formatters';
import { checkMoveToAdmin } from '@/utils/user';
import { useAnewsStorage } from '@/utils/user-document/anewsStorage';
import {
  AdminTableItem,
  displaySyncStatus,
  isSynced,
  isSyncing,
} from '@/utils/user-document/storage';

const router = useRouter();
const { createSnackbar } = useSnackbar();
const { updateDetails: updateDetailsAnewsStorage } = useAnewsStorage();

const showFileListKey = ref(0);
const adminTableItem = ref<AdminTableItem>({
  id: 3,
  storageType: 'anews_storage',
  isLoaded: false,
  isConnected: false,
  syncStatus: '-',
  fileCount: '-',
  syncStartedAt: '-',
  syncEndedAt: '-',
  connectText: 'ファイルをアップロード',
  status: 'NOT_CONNECTED',
});

const fields = computed<Field<AdminTableItem>[]>(() => [
  {
    name: 'syncStatus',
    displayName: 'ステータス',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'fileCount',
    displayName: '同期済みのファイル数',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncStartedAt',
    displayName: '同期開始時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncEndedAt',
    displayName: '同期終了時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'actions',
    displayName: '',
    isSortable: false,
    type: 'string',
    fieldPosition: 'right',
    cellPosition: 'right',
  },
]);

// 連携状態を更新
const updateStorageStatus = () => {
  updateDetailsAnewsStorage(adminTableItem.value);
  showFileListKey.value++;
};

onMounted(async () => {
  await checkMoveToAdmin(router);
  const teamInfo = await api.fetchTeamInfo();

  if (!teamInfo.enable_user_document) {
    createSnackbar({
      message: '権限がありません',
      type: 'error',
    });
    await router.push({ name: 'anewsHome' });
  }

  updateStorageStatus();
});
</script>

<template>
  <UserDocument>
    <div class="c-title-bold--m">Aconnectストレージ</div>
    <div class="container">
      <AdminBaseTable
        :is-stricky-header="true"
        :fields="fields"
        :items="[adminTableItem]"
        action-width="240px"
      >
        >
        <template #header-fileCount="{ item }">
          <div class="header-title-with-icon">
            <div>{{ item.displayName }}</div>
            <DgrIcon
              size="small"
              name="question-circle"
              v-tooltip.top="{
                content:
                  '連携中のファイルはPDF/Word/PowerPoint/Excelのみとなります',
                theme: 'menu',
              }"
            />
          </div>
        </template>
        <template #cell-syncStatus="{ item }">
          <DgrIcon
            :keep-fill="false"
            name="chart-round-bubble"
            v-if="isSyncing(item.status)"
            class="name-icon syncing"
          />
          <DgrIcon
            :keep-fill="false"
            name="check"
            v-else-if="isSynced(item.status)"
            class="name-icon synced"
          />
          <span v-text="displaySyncStatus(item)"></span>
        </template>
        <template #cell-fileCount="{ item }">
          <div class="header-title-with-icon"></div>
          <span v-text="item.fileCount"></span>
        </template>
        <template #cell-syncStartedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncStartedAt)"></span>
        </template>
        <template #cell-syncEndedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncEndedAt)"></span>
        </template>
      </AdminBaseTable>
      <AnewsStorageUploadFileFolderForm
        :processing="adminTableItem.status === 'SYNCING'"
        @upload-callback="updateStorageStatus"
      />
      <AnewsStorageFileList
        :key="showFileListKey"
        :processing="adminTableItem.status === 'SYNCING'"
        @delete-callback="updateStorageStatus"
      />
    </div>
  </UserDocument>
</template>

<style scoped lang="scss">
.container {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 100px; // ステージング環境などで表示されるZendeskのサポートウィジェット用のマージン

  .header-title-with-icon {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    align-items: center;
  }

  .name-icon {
    display: inline-block;
    margin-right: 10px;
    vertical-align: middle;

    &.syncing {
      color: $color-gray800;
    }
    &.synced {
      color: $color-green600;
    }
    &.sync-failed {
      color: $color-orange1000;
    }
  }

  .action-icon {
    display: flex;
    justify-content: center;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background-color: $color-gray200;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
  }

  .action-button {
    white-space: nowrap;
    margin-left: auto;
  }

  .connect-failed {
    text-align: right;
    color: $color-orange1000;
  }

  .actions {
    display: inline-block;
  }

  .item {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px 12px;
    &:hover {
      background: $color-gray400;
    }
    &.disabled {
      color: $color-gray600;
      cursor: not-allowed;
    }
    .icon {
      margin-right: 4px;
    }
  }
}
</style>
